import React, { useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, Pressable, Platform, ActivityIndicator } from 'react-native';
import {
  ManageAccountsSearchBar,
  DEFAULT_ACCOUNT_BLOCKSIZE,
  type ManageAccountsSearchValues,
} from '../account/ManageAccountsSearchBar';
import { AdminMigrateService } from '../../../services/AdminMigrateService';
import { AdminAssessmentStorage } from '../../../services/AdminAssessmentStorage';
import type { PracSearchRequestDto, PracSearchResultDto, AdminAssessmentInfoDto } from '../../../types/api';
import type { AdminAssessmentDto } from '../../../types/adminAssessment';
import { useActiveAccountStore } from '../../../stores/activeAccountStore';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { AccountFilterSelect } from '../AccountFilterSelect';

interface MigrateViewProps {
  apiService?: any;
  storage?: any;
}

interface SourceAccount {
  accountId: string;
  accountName: string;
  ownerEmail: string;
  status: string;
}

const EMPTY_SEARCH: ManageAccountsSearchValues = { accountName: '', accountId: '', accountGroup: '', billpayProcessor: '', showAll: false, blocksize: DEFAULT_ACCOUNT_BLOCKSIZE };

const MAX_CONTENT_WIDTH = 1000;

const RECOMMENDED_PROFILE_BLOCK_SIZE = 100;
const RECOMMENDED_DATA_BLOCK_SIZE = 500;
const PROFILE_BLOCK_SIZE_OPTIONS = [25, 50, 100, 200, 500];
const DATA_BLOCK_SIZE_OPTIONS = [100, 250, 500, 1000, 2000];

type MigrateStatus = 'idle' | 'practice' | 'confirm' | 'running' | 'paused' | 'done' | 'cancelled' | 'error';

interface MigrateProgress {
  pracResult: string; // Result from admin_migrate_prac (e.g. Full or Update)
  profilesTotal: number;
  dataTotal: number;
  launchTotal: number;
  assessments: AdminAssessmentInfoDto[];
  profileBlockSize: number;
  profileBlock: number; // next profile block to send (== blocks completed)
  profileBlocks: number;
  dataBlockSize: number;
  dataBlock: number; // next data block to send (== blocks completed)
  dataBlocks: number;
  dataStartDate: string;
  siteTrans: string;
  lastResult: string;
}

const EMPTY_PROGRESS: MigrateProgress = {
  pracResult: '',
  profilesTotal: 0,
  dataTotal: 0,
  launchTotal: 0,
  assessments: [],
  profileBlockSize: RECOMMENDED_PROFILE_BLOCK_SIZE,
  profileBlock: 0,
  profileBlocks: 0,
  dataBlockSize: RECOMMENDED_DATA_BLOCK_SIZE,
  dataBlock: 0,
  dataBlocks: 0,
  dataStartDate: '',
  siteTrans: '',
  lastResult: '',
};

const BlockSizeSelect: React.FC<{
  value: number;
  options: number[];
  recommended: number;
  onChange: (value: number) => void;
  label: string;
}> = ({ value, options, recommended, onChange, label }) => {
  const labelFor = (option: number) => (option === recommended ? `${option} (recommended)` : String(option));

  if (Platform.OS === 'web') {
    return React.createElement(
      'select' as any,
      {
        value: String(value),
        onChange: (event: any) => onChange(Number.parseInt(String(event?.target?.value ?? ''), 10) || recommended),
        style: { ...webControlStyle, minWidth: 150 },
        'aria-label': label,
      },
      options.map((option) => React.createElement('option' as any, { key: option, value: String(option) }, labelFor(option)))
    );
  }

  const cycle = () => {
    const index = options.indexOf(value);
    onChange(options[(index + 1) % options.length]);
  };
  return (
    <Pressable onPress={cycle} style={styles.nativeSelect} accessibilityRole="button" accessibilityLabel={label}>
      <Text style={styles.cellText}>{labelFor(value)} ▼</Text>
    </Pressable>
  );
};

const AssessmentSelect: React.FC<{
  value: string;
  options: AdminAssessmentDto[];
  onChange: (value: string) => void;
}> = ({ value, options, onChange }) => {
  const labelFor = (sourceId: string) => options.find((option) => option.sourceId === sourceId)?.dName || sourceId;

  if (Platform.OS === 'web') {
    return React.createElement(
      'select' as any,
      {
        value,
        onChange: (event: any) => onChange(String(event?.target?.value ?? '')),
        style: { ...webControlStyle, minWidth: 220 },
        'aria-label': 'Assessment',
      },
      [
        React.createElement('option' as any, { key: '', value: '' }, 'None'),
        ...options.map((option) =>
          React.createElement('option' as any, { key: option.sourceId, value: option.sourceId }, option.dName || option.sourceId)
        ),
      ]
    );
  }

  const cycle = () => {
    const ids = ['', ...options.map((option) => option.sourceId)];
    const index = ids.indexOf(value);
    onChange(ids[(index + 1) % ids.length]);
  };
  return (
    <Pressable onPress={cycle} style={styles.nativeSelect} accessibilityRole="button" accessibilityLabel="Assessment">
      <Text style={styles.cellText}>{value ? labelFor(value) : 'None'} ▼</Text>
    </Pressable>
  );
};

const toCount = (value: string | undefined): number => {
  const parsed = Number.parseInt((value ?? '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const blocksFor = (total: number, blockSize: number): number => Math.ceil(total / blockSize);

/**
 * Admin > Migrate. The destination is picked with the Select Account dropdown (defaulting to the
 * active account; create/configure it first via Account > Manage Accounts > Add New Account); the source is picked here
 * from an admin_migrate_search result list.
 *
 * Migrating runs admin_migrate_prac (practice info; returns Result Full/Update plus profile/data/
 * launch totals), then waits on a confirm step with two independent transfers the user can run in
 * either order: Transfer Profiles (admin_migrate_profiles, its own block size) and Transfer Data
 * (admin_migrate_data, carrying ReqSurveybId and AssessmentId — AssessmentId picked from the
 * bundle-content assessments in local storage whose SurveybList contains that survey id). Each
 * runs its own zero-based block loop; the profile block count is fixed at ceil(total / blockSize)
 * since admin_migrate_profiles doesn't report a max, while the data block count is replaced by the
 * response's DataBlockNumMax whenever that is > 0. activeDimension tracks which of the two is
 * currently running/paused so Pause/Resume/Cancel act on the right one; status only reaches 'done'
 * once both have exhausted their blocks. Pause/Cancel take effect between requests.
 */
export const MigrateView: React.FC<MigrateViewProps> = ({ apiService, storage }) => {
  const [searchValues, setSearchValues] = useState<ManageAccountsSearchValues>(EMPTY_SEARCH);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SourceAccount[]>([]);
  const [source, setSource] = useState<SourceAccount | null>(null);
  const [reqSurveybId, setReqSurveybId] = useState('');

  const [assessmentOptions, setAssessmentOptions] = useState<AdminAssessmentDto[]>([]);
  const [assessmentId, setAssessmentId] = useState('');
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const adminAssessmentStorage = useMemo(() => (storage ? new AdminAssessmentStorage(storage) : null), [storage]);

  const [status, setStatus] = useState<MigrateStatus>('idle');
  const [activeDimension, setActiveDimension] = useState<'profiles' | 'data' | null>(null);
  const [progress, setProgress] = useState<MigrateProgress>(EMPTY_PROGRESS);
  const [migrateError, setMigrateError] = useState<string | null>(null);
  const progressRef = useRef<MigrateProgress>(EMPTY_PROGRESS);
  const pauseRequestedRef = useRef(false);
  const cancelRequestedRef = useRef(false);
  const practiceDoneRef = useRef(false);
  const runIdsRef = useRef<{ src: string; dest: string }>({ src: '', dest: '' });

  // Destination is chosen with the Select Account dropdown below; it defaults to the active
  // account but picking one here doesn't switch the active account.
  const activeAccountId = useActiveAccountStore((state) => state.activeAccountId);
  const activeAccountName = useActiveAccountStore((state) => state.activeAccountName);
  const [selectedDestination, setSelectedDestination] = useState<{ id: string; name: string } | null>(null);
  const destinationId = selectedDestination?.id ?? activeAccountId;
  const destinationName = selectedDestination ? selectedDestination.name : activeAccountName;

  const updateProgress = (next: MigrateProgress) => {
    progressRef.current = next;
    setProgress(next);
  };

  /** Called once a dimension's loop runs out of blocks; 'done' only once both have. */
  const finishDimension = () => {
    const current = progressRef.current;
    const profilesComplete = current.profileBlock >= current.profileBlocks;
    const dataComplete = current.dataBlock >= current.dataBlocks;
    setActiveDimension(null);
    setStatus(profilesComplete && dataComplete ? 'done' : 'confirm');
  };

  const runProfileBlocks = async () => {
    const service = new AdminMigrateService(apiService);
    const { src, dest } = runIdsRef.current;
    pauseRequestedRef.current = false;
    setActiveDimension('profiles');
    setStatus('running');
    setMigrateError(null);

    try {
      while (true) {
        if (cancelRequestedRef.current) {
          setStatus('cancelled');
          return;
        }
        if (pauseRequestedRef.current) {
          setStatus('paused');
          return;
        }

        const current = progressRef.current;
        if (current.profileBlock >= current.profileBlocks) {
          finishDimension();
          return;
        }

        const response = await service.migrateProfileBlock({
          SrcPracId: src,
          DestPracId: dest,
          BlockSize: String(current.profileBlockSize),
          BlockNum: String(current.profileBlock),
        });

        updateProgress({
          ...current,
          profileBlock: current.profileBlock + 1,
          lastResult: response.Result || '',
        });
      }
    } catch (err: any) {
      console.error('MigrateView: profile block transfer failed', err);
      setMigrateError(err?.message || 'Profile transfer failed');
      setStatus('error');
    }
  };

  const runDataBlocks = async () => {
    const service = new AdminMigrateService(apiService);
    const { src, dest } = runIdsRef.current;
    pauseRequestedRef.current = false;
    setActiveDimension('data');
    setStatus('running');
    setMigrateError(null);

    try {
      while (true) {
        if (cancelRequestedRef.current) {
          setStatus('cancelled');
          return;
        }
        if (pauseRequestedRef.current) {
          setStatus('paused');
          return;
        }

        const current = progressRef.current;
        if (current.dataBlock >= current.dataBlocks) {
          finishDimension();
          return;
        }

        const response = await service.migrateDataBlock({
          SrcPracId: src,
          DestPracId: dest,
          ReqSurveybId: reqSurveybId.trim(),
          AssessmentId: assessmentId,
          DataBlockSize: String(current.dataBlockSize),
          DataBlockNum: String(current.dataBlock),
          DataStartDate: current.dataStartDate,
          SiteTrans: current.siteTrans,
        });

        const dataMax = toCount(response.DataBlockNumMax);
        updateProgress({
          ...current,
          dataBlock: current.dataBlock + 1,
          dataBlocks: dataMax > 0 ? dataMax : current.dataBlocks,
          lastResult: response.Result || '',
        });
      }
    } catch (err: any) {
      console.error('MigrateView: data block transfer failed', err);
      setMigrateError(err?.message || 'Data transfer failed');
      setStatus('error');
    }
  };

  const handleMigrate = async () => {
    if (!apiService || !source || !destinationId) {
      return;
    }

    cancelRequestedRef.current = false;
    pauseRequestedRef.current = false;
    runIdsRef.current = { src: source.accountId, dest: destinationId };
    practiceDoneRef.current = false;
    updateProgress(EMPTY_PROGRESS);
    setMigrateError(null);
    setAssessmentId('');
    setAssessmentOptions([]);
    setActiveDimension(null);
    setStatus('practice');

    const wantedSurveyId = reqSurveybId.trim();

    try {
      const response = await new AdminMigrateService(apiService).migratePractice({
        SrcPracId: source.accountId,
        DestPracId: destinationId,
        ReqSurveybId: wantedSurveyId,
      });

      const profilesTotal = toCount(response.ProfilesTotal);
      const dataTotal = toCount(response.DataTotal);
      const launchTotal = toCount(response.LaunchTotal);
      updateProgress({
        pracResult: response.Result || '',
        profilesTotal,
        dataTotal,
        launchTotal,
        assessments: response.Assessments || [],
        profileBlockSize: RECOMMENDED_PROFILE_BLOCK_SIZE,
        profileBlock: 0,
        profileBlocks: blocksFor(profilesTotal, RECOMMENDED_PROFILE_BLOCK_SIZE),
        dataBlockSize: RECOMMENDED_DATA_BLOCK_SIZE,
        dataBlock: 0,
        dataBlocks: blocksFor(dataTotal, RECOMMENDED_DATA_BLOCK_SIZE),
        dataStartDate: response.DataStartDate || '',
        siteTrans: response.SiteTrans || '',
        lastResult: '',
      });
      practiceDoneRef.current = true;
      setStatus('confirm');

      if (adminAssessmentStorage && wantedSurveyId) {
        setAssessmentsLoading(true);
        try {
          const allAssessments = await adminAssessmentStorage.getAvailableAssessments();
          const matches = allAssessments.filter((assessment) =>
            (assessment.surveybList || '')
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean)
              .includes(wantedSurveyId)
          );
          setAssessmentOptions(matches);
        } catch (err) {
          console.error('MigrateView: failed to load assessment options', err);
          setAssessmentOptions([]);
        } finally {
          setAssessmentsLoading(false);
        }
      }
    } catch (err: any) {
      console.error('MigrateView: practice migration failed', err);
      setMigrateError(err?.message || 'Practice migration failed');
      setStatus('error');
    }
  };

  const handleProfileBlockSizeChange = (size: number) => {
    const current = progressRef.current;
    updateProgress({ ...current, profileBlockSize: size, profileBlocks: blocksFor(current.profilesTotal, size) });
  };

  const handleDataBlockSizeChange = (size: number) => {
    const current = progressRef.current;
    updateProgress({ ...current, dataBlockSize: size, dataBlocks: blocksFor(current.dataTotal, size) });
  };

  const handleTransferProfiles = () => {
    void runProfileBlocks();
  };

  const handleTransferData = () => {
    void runDataBlocks();
  };

  const handlePause = () => {
    pauseRequestedRef.current = true;
  };

  const handleResume = () => {
    if (activeDimension === 'profiles') {
      void runProfileBlocks();
    } else if (activeDimension === 'data') {
      void runDataBlocks();
    }
  };

  const handleCancel = () => {
    cancelRequestedRef.current = true;
    // Nothing is in flight while paused/errored, so there's no loop to notice the flag.
    if (status === 'confirm' || status === 'paused' || status === 'error') {
      setStatus('cancelled');
    }
  };

  const handleSearch = async (values: ManageAccountsSearchValues) => {
    setIsLoading(true);
    setHasSearched(true);
    setError(null);

    try {
      if (!apiService) {
        throw new Error('API service not available');
      }

      const request: PracSearchRequestDto = {
        blocknum: '0',
        blocksize: '100',
        searchTerms: [],
        searchFilters: [],
      };

      if (values.accountName.trim()) {
        request.searchTerms.push({ searchField: 'accountname', searchTerm: values.accountName.trim() });
      }
      if (values.accountId.trim()) {
        request.searchTerms.push({ searchField: 'accountid', searchTerm: values.accountId.trim() });
      }
      if (values.showAll) {
        request.searchFilters.push({ searchField: 'showall', searchFilter: '1' });
      }

      const response = await new AdminMigrateService(apiService).searchSourceAccounts(request);

      setResults(
        response.results.map((result: PracSearchResultDto) => ({
          accountId: result.AccountId,
          accountName: result.DName || `Account ${result.AccountId}`,
          ownerEmail: result.Email || '-',
          status: result.Status || '-',
        }))
      );
    } catch (err: any) {
      console.error('MigrateView: source account search failed', err);
      setError(err?.message || 'Search failed');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const sourceIsDestination = !!source && source.accountId === destinationId;
  const isBusy = status === 'practice' || status === 'confirm' || status === 'running' || status === 'paused';
  const canMigrate = !!source && !!destinationId && !sourceIsDestination && !isBusy && !!apiService;
  const showProgress = status !== 'idle';
  const canResume = status === 'paused' || (status === 'error' && practiceDoneRef.current);
  const profilesComplete = progress.profileBlock >= progress.profileBlocks;
  const dataComplete = progress.dataBlock >= progress.dataBlocks;

  const dimensionLabel = activeDimension === 'profiles' ? 'profiles' : activeDimension === 'data' ? 'data' : '';
  const statusLabel: Record<MigrateStatus, string> = {
    idle: '',
    practice: 'Migrating practice info...',
    confirm: 'Practice info migrated — review and confirm the transfers',
    running: `Transferring ${dimensionLabel}...`,
    paused: `Paused${dimensionLabel ? ` (${dimensionLabel})` : ''}`,
    done: 'Migration complete',
    cancelled: 'Cancelled — blocks already transferred were not rolled back',
    error: 'Stopped by an error',
  };

  return (
    <View style={styles.container}>
      <View style={styles.summary}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Select Account</Text>
          <AccountFilterSelect
            value={destinationId}
            onChange={(id, name) => setSelectedDestination({ id, name })}
            disabled={isBusy}
          />
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Source</Text>
          <Text style={styles.summaryValue}>
            {source ? `${source.accountName} (${source.accountId})` : 'Search below and select the source account'}
          </Text>
          <Text style={styles.inlineLabel}>Required</Text>
          <TextInput
            style={styles.inlineInput}
            value={reqSurveybId}
            onChangeText={setReqSurveybId}
            editable={!isBusy}
            autoCapitalize="none"
            placeholder="Survey ID"
            placeholderTextColor={EDITOR_COLORS.placeholder}
            accessibilityLabel="Required survey ID"
          />
          <Pressable
            onPress={() => { void handleMigrate(); }}
            disabled={!canMigrate}
            style={[styles.actionBtn, styles.migrateBtn, !canMigrate && styles.actionBtnDisabled]}
            accessibilityRole="button"
            accessibilityLabel="Migrate practice"
          >
            <Text style={styles.migrateBtnText}>Migrate</Text>
          </Pressable>
        </View>
        {sourceIsDestination ? (
          <Text style={styles.warningText}>The source and destination accounts must be different.</Text>
        ) : null}

        {showProgress ? (
          <View style={styles.progressPanel}>
            <View style={styles.progressHeader}>
              {status === 'practice' || status === 'running' ? (
                <ActivityIndicator size="small" color={EDITOR_COLORS.accent} />
              ) : null}
              <Text style={[styles.progressStatus, status === 'error' && styles.errorText]}>
                {statusLabel[status]}
              </Text>
              {status === 'running' ? (
                <Pressable onPress={handlePause} style={styles.actionBtn} accessibilityRole="button" accessibilityLabel="Pause migration">
                  <Text style={styles.actionBtnText}>Pause</Text>
                </Pressable>
              ) : null}
              {canResume ? (
                <Pressable onPress={handleResume} style={styles.actionBtn} accessibilityRole="button" accessibilityLabel="Resume migration">
                  <Text style={styles.actionBtnText}>Resume</Text>
                </Pressable>
              ) : null}
              {status === 'confirm' ? (
                <Pressable
                  onPress={handleTransferProfiles}
                  disabled={profilesComplete}
                  style={[styles.actionBtn, styles.migrateBtn, profilesComplete && styles.actionBtnDisabled]}
                  accessibilityRole="button"
                  accessibilityLabel="Transfer profiles"
                >
                  <Text style={styles.migrateBtnText}>Transfer Profiles</Text>
                </Pressable>
              ) : null}
              {status === 'confirm' ? (
                <Pressable
                  onPress={handleTransferData}
                  disabled={dataComplete}
                  style={[styles.actionBtn, styles.migrateBtn, dataComplete && styles.actionBtnDisabled]}
                  accessibilityRole="button"
                  accessibilityLabel="Transfer data"
                >
                  <Text style={styles.migrateBtnText}>Transfer Data</Text>
                </Pressable>
              ) : null}
              {status === 'confirm' || status === 'running' || status === 'paused' || status === 'error' ? (
                <Pressable onPress={handleCancel} style={styles.actionBtn} accessibilityRole="button" accessibilityLabel="Cancel migration">
                  <Text style={styles.actionBtnText}>Cancel</Text>
                </Pressable>
              ) : null}
            </View>
            {status === 'confirm' ? (
              <View style={styles.confirmGrid}>
                <Text style={styles.progressLine}>
                  Result: {progress.pracResult || '-'}
                  {progress.dataStartDate ? `  (data from ${progress.dataStartDate})` : ''}
                </Text>
                <View style={styles.confirmRow}>
                  <Text style={styles.confirmLabel}>Profiles</Text>
                  <Text style={styles.confirmCount}>{progress.profilesTotal} records</Text>
                  <BlockSizeSelect
                    label="Profile block size"
                    value={progress.profileBlockSize}
                    options={PROFILE_BLOCK_SIZE_OPTIONS}
                    recommended={RECOMMENDED_PROFILE_BLOCK_SIZE}
                    onChange={handleProfileBlockSizeChange}
                  />
                  <Text style={styles.confirmBlocks}>{progress.profileBlocks} blocks</Text>
                </View>
                <View style={styles.confirmRow}>
                  <Text style={styles.confirmLabel}>Data</Text>
                  <Text style={styles.confirmCount}>{progress.dataTotal} records</Text>
                  <BlockSizeSelect
                    label="Data block size"
                    value={progress.dataBlockSize}
                    options={DATA_BLOCK_SIZE_OPTIONS}
                    recommended={RECOMMENDED_DATA_BLOCK_SIZE}
                    onChange={handleDataBlockSizeChange}
                  />
                  <Text style={styles.confirmBlocks}>{progress.dataBlocks} blocks</Text>
                </View>
                <View style={styles.confirmRow}>
                  <Text style={styles.confirmLabel}>Assessment</Text>
                  {assessmentsLoading ? (
                    <Text style={styles.confirmCount}>Loading...</Text>
                  ) : assessmentOptions.length > 0 ? (
                    <AssessmentSelect value={assessmentId} options={assessmentOptions} onChange={setAssessmentId} />
                  ) : (
                    <Text style={styles.confirmCount}>No matching bundle-content assessments</Text>
                  )}
                </View>
                <View style={styles.confirmRow}>
                  <Text style={styles.confirmLabel}>Launch</Text>
                  <Text style={styles.confirmCount}>{progress.launchTotal} pending</Text>
                </View>

                {progress.assessments.length > 0 ? (
                  <View style={styles.assessmentsTable}>
                    <View style={styles.assessmentsHeaderRow}>
                      <Text style={styles.assessmentsHeaderText}>Assessment</Text>
                      <Text style={[styles.assessmentsHeaderText, styles.assessmentsDataTotalCol]}>Data Total</Text>
                    </View>
                    {progress.assessments.map((assessment, index) => (
                      <View key={`${assessment.SurveyAliasList}-${index}`} style={styles.assessmentsRow}>
                        <Text style={styles.assessmentsCellText}>{assessment.SurveyAliasList || '-'}</Text>
                        <Text style={[styles.assessmentsCellText, styles.assessmentsDataTotalCol]}>
                          {assessment.DataTotal || '0'}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                <Text style={styles.progressLine}>
                  Transfer Profiles and Transfer Data move {source?.accountName} into {destinationName || destinationId}
                  {' '}independently — run them in either order, and each can be paused and resumed on its own.
                </Text>
              </View>
            ) : status !== 'practice' ? (
              <>
                <Text style={styles.progressLine}>
                  Profiles: block {Math.min(progress.profileBlock, progress.profileBlocks)} of {progress.profileBlocks}
                  {' '}({progress.profilesTotal} total)
                </Text>
                <Text style={styles.progressLine}>
                  Data: block {Math.min(progress.dataBlock, progress.dataBlocks)} of {progress.dataBlocks}
                  {' '}({progress.dataTotal} total{progress.dataStartDate ? `, from ${progress.dataStartDate}` : ''})
                </Text>
                {progress.launchTotal > 0 ? (
                  <Text style={styles.progressLine}>Launch: {progress.launchTotal} pending</Text>
                ) : null}
              </>
            ) : null}
            {progress.lastResult ? <Text style={styles.progressLine}>Result: {progress.lastResult}</Text> : null}
            {migrateError ? <Text style={styles.errorLine}>{migrateError}</Text> : null}
          </View>
        ) : null}
      </View>

      <ManageAccountsSearchBar
        values={searchValues}
        onValuesChange={setSearchValues}
        onSearch={handleSearch}
        isLoading={isLoading}
        onAddNewAccount={() => {}}
      />

      <ScrollView style={styles.resultsContainer}>
        <View style={styles.resultsInner}>
          {error ? (
            <View style={styles.messageContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : !hasSearched ? (
            <View style={styles.messageContainer}>
              <Text style={styles.messageText}>Enter search criteria and click Search to find the source account</Text>
            </View>
          ) : isLoading ? (
            <View style={styles.messageContainer}>
              <Text style={styles.messageText}>Searching...</Text>
            </View>
          ) : results.length === 0 ? (
            <View style={styles.messageContainer}>
              <Text style={styles.messageText}>No accounts found</Text>
            </View>
          ) : (
            <View style={styles.tableContainer}>
              <View style={styles.tableHeader}>
                <View style={[styles.tableCell, styles.checkmarkCol]} />
                <View style={[styles.tableCell, styles.accountNameCol]}>
                  <Text style={styles.headerText}>Account Name</Text>
                </View>
                <View style={[styles.tableCell, styles.ownerEmailCol]}>
                  <Text style={styles.headerText}>Owner Email</Text>
                </View>
                <View style={[styles.tableCell, styles.accountIdCol]}>
                  <Text style={styles.headerText}>Account Id</Text>
                </View>
                <View style={[styles.tableCell, styles.statusCol]}>
                  <Text style={styles.headerText}>Status</Text>
                </View>
              </View>

              {results.map((result, index) => {
                const isSelected = result.accountId === source?.accountId;
                return (
                  <Pressable
                    key={result.accountId}
                    onPress={() => { if (!isBusy) setSource(result); }}
                    style={({ pressed }) => [
                      styles.tableRow,
                      index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd,
                      pressed && styles.tableRowHover,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={`Select source account ${result.accountName}`}
                  >
                    <View style={[styles.tableCell, styles.checkmarkCol]}>
                      {isSelected && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <View style={[styles.tableCell, styles.accountNameCol]}>
                      <Text style={styles.cellText}>{result.accountName}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.ownerEmailCol]}>
                      <Text style={styles.cellText}>{result.ownerEmail}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.accountIdCol]}>
                      <Text style={styles.cellText}>{result.accountId}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.statusCol]}>
                      <Text style={styles.cellText}>{result.status}</Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  confirmGrid: {
    marginTop: 4,
  },
  confirmRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  confirmLabel: {
    ...editorText,
    width: 80,
    color: EDITOR_COLORS.textPrimary,
  },
  confirmCount: {
    ...editorText,
    width: 130,
    color: EDITOR_COLORS.textSecondary,
  },
  confirmBlocks: {
    ...editorText,
    marginLeft: 12,
    color: EDITOR_COLORS.textSecondary,
  },
  assessmentsTable: {
    marginTop: 10,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: EDITOR_COLORS.background,
  },
  assessmentsHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  assessmentsHeaderText: {
    ...editorText,
    flex: 1,
    color: EDITOR_COLORS.textSecondary,
  },
  assessmentsRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  assessmentsCellText: {
    ...editorText,
    flex: 1,
    color: EDITOR_COLORS.textPrimary,
  },
  assessmentsDataTotalCol: {
    flex: 0,
    minWidth: 100,
    textAlign: 'right',
  },
  nativeSelect: {
    minWidth: 150,
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    justifyContent: 'center',
  },
  summary: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 4,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  summaryLabel: {
    ...editorText,
    width: 90,
    color: EDITOR_COLORS.textSecondary,
  },
  summaryValue: {
    ...editorText,
    flex: 1,
    color: EDITOR_COLORS.textPrimary,
  },
  warningText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginTop: 2,
  },
  inlineLabel: {
    ...editorText,
    marginLeft: 10,
    color: EDITOR_COLORS.textSecondary,
  },
  inlineInput: {
    ...editorText,
    width: 140,
    height: 30,
    marginLeft: 6,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 6,
    backgroundColor: EDITOR_COLORS.background,
    color: EDITOR_COLORS.textPrimary,
  },
  resultsContainer: {
    flex: 1,
  },
  resultsInner: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  actionBtn: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    marginLeft: 10,
    backgroundColor: EDITOR_COLORS.submitBackground,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
    opacity: 0.6,
  },
  actionBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  migrateBtn: {
    backgroundColor: EDITOR_COLORS.action,
  },
  migrateBtnText: {
    ...editorText,
    color: EDITOR_COLORS.actionText,
  },
  progressPanel: {
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  progressStatus: {
    ...editorText,
    flex: 1,
    marginLeft: 6,
    color: EDITOR_COLORS.textPrimary,
  },
  progressLine: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    marginTop: 2,
  },
  errorLine: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginTop: 4,
  },
  messageContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  messageText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    textAlign: 'center',
  },
  tableContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  tableRowEven: {
    backgroundColor: EDITOR_COLORS.background,
  },
  tableRowOdd: {
    backgroundColor: '#f9fafb',
  },
  tableRowHover: {
    backgroundColor: '#eff6ff',
    cursor: Platform.OS === 'web' ? 'pointer' : undefined,
  } as any,
  tableCell: {
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  checkmarkCol: {
    flex: 0,
    minWidth: 40,
    alignItems: 'center',
  },
  checkmark: {
    fontSize: 16,
    color: EDITOR_COLORS.successText,
  },
  accountNameCol: {
    flex: 3,
    minWidth: 180,
  },
  ownerEmailCol: {
    flex: 3,
    minWidth: 200,
  },
  accountIdCol: {
    flex: 1,
    minWidth: 100,
  },
  statusCol: {
    flex: 1,
    minWidth: 100,
  },
  headerText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  cellText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
});
